// Operational Engineering — Well State projector.
//
// Pure read-only function. Given a wellId (and optionally a specific BHA /
// drill-string / rig-sheet id), returns a snapshot of "live well state" that
// Module 4.1 (Kill Sheets) and every future operational-engineering module
// consume as inputs.
//
// Anything we cannot resolve is left as "" (empty string) — the caller's
// validation surface decides whether that input is required.

import { getWellProfile } from "@/lib/well/storage";
import { getBha, listBhasForWell } from "@/lib/bha/storage";
import { getDrillString, listDrillStringsForWell } from "@/lib/drillstring/storage";
import { computeDrillStringGeometry, BBL_PER_FT_DIVISOR } from "@/lib/drillstring/schema";
import { roleLabel } from "@/lib/bha/schema";
import { findById } from "@/lib/reflibs/lookup";
import { casingIdIntervals, resolveOpenHoleDiameterIn } from "@/lib/well/casingGeometry";

/**
 * Pull the live well state for a given well. Optional explicit IDs override
 * the auto-pick (e.g. forcing a specific BHA snapshot for a historical sheet).
 */
export function buildWellState({ wellId, bhaId, drillStringId, rigSheetId } = {}) {
  if (!wellId) return _emptyState();
  const well = getWellProfile(wellId);
  if (!well) return _emptyState();

  // Auto-pick the active BHA when none was explicitly supplied:
  // prefer `operationalStatus === "in-hole"`, otherwise the most-recently
  // updated BHA on this well.
  let bha = null;
  if (bhaId) {
    const candidate = getBha(bhaId);
    if (candidate && candidate.wellId === wellId) bha = candidate;
  }
  if (!bha) {
    const all = listBhasForWell(wellId);
    bha = all.find((b) => b.operationalStatus === "in-hole") || all[0] || null;
  }

  // Active Drill String — same pick policy.
  let ds = null;
  if (drillStringId) {
    const candidate = getDrillString(drillStringId);
    if (candidate && candidate.wellId === wellId) ds = candidate;
  }
  if (!ds) {
    const all = listDrillStringsForWell(wellId);
    ds = all.find((d) => d.operationalStatus === "in-hole") || all[0] || null;
  }

  // Current depth: prefer the well's totalDepthCurrentFt, then the deepest
  // casing-row bottomDepth, then the BHA depthOutFt.
  const csg = well.children?.casingProgram || [];
  const csgMaxBottom = csg.reduce((acc, r) => {
    const n = Number(r.bottomDepthFt);
    return Number.isFinite(n) && n > acc ? n : acc;
  }, 0);
  const currentMdFt =
    _firstFinite(well.totalDepthCurrentFt, csgMaxBottom, bha?.depthOutFt) ?? "";

  // Casing shoe depth: deepest casing row's bottomDepth, if any.
  const casingShoeMd = csgMaxBottom > 0 ? csgMaxBottom : "";

  // Open-hole diameter: the Well Profile's MANUAL override first, then the
  // bound BHA's bit size. A casing row's NOMINAL size is deliberately not
  // used as a substitute — it is a label, not a drilled diameter.
  const openHole = resolveOpenHoleDiameterIn({ well, bha });
  const holeSize = openHole.value ?? "";

  // String internal volume — DrillString sections (above BHA) + BHA components.
  // Only emitted when ALL contributing components carry the ID + length they
  // need. If any are missing, returns "" so the Kill Sheet's re-pull leaves
  // the user's manual input untouched.
  const stringInternal = _computeStringInternalVolBbl(ds, bha);
  const stringInternalVolBbl = stringInternal.value;

  // Annular volumes — bit→shoe (open-hole) + shoe→surface (cased).
  // Returns "" cells when contributing geometry is incomplete.
  const annular = _computeAnnularVolumes({
    well, bha, ds,
    bitMd: currentMdFt, shoeMd: casingShoeMd, holeSizeIn: holeSize,
    holeSizeIssue: openHole.invalid
      || (openHole.value == null
        ? "open-hole diameter unknown — enter it on the Well Profile (Depths & Datums) or bind a BHA with a bit size"
        : ""),
  });

  return {
    wellId,
    well: {
      wellName: well.wellName, operator: well.operator,
      rigName: well.rigName, rigContractor: well.rigContractor,
      environment: well.environment, status: well.status,
    },
    sources: {
      bhaId:         bha?.id || "",
      drillStringId: ds?.id  || "",
      rigSheetId:    rigSheetId || "",
    },
    snapshot: {
      currentMdFt,
      currentTvdFt: currentMdFt,         // until Module 4.1b adds a deviation survey
      holeSize,
      casingShoeMd,
      casingShoeTvdFt: casingShoeMd,     // same caveat as above
      stringInternalVolBbl,
      bitToShoeVolBbl:       annular.bitToShoeVolBbl,
      shoeToSurfaceVolBbl:   annular.shoeToSurfaceVolBbl,
      casingInnerDiameterIn: annular.casingId,
      // Completeness flags — a partial total is never presented as complete.
      stringInternalVolComplete: stringInternal.complete,
      stringInternalVolMissing:  stringInternal.missing,
      annularVolComplete:        annular.complete,
      annularVolIssues:          annular.issues,
      assemblyLengthFt:          annular.assemblyLengthFt,
    },
    // Component-level audit trail (not copied into Kill Sheet snapshots —
    // the Kill Sheet consumes only the numeric paths above).
    breakdown: {
      stringInternal: stringInternal.breakdown,
      annulus:        annular.breakdown,
    },
  };
}

// ---------------------------------------------------------------------------
// Component geometry resolution — ONE place that decides which ID / OD a
// component contributes, and where that number came from.
//
// Priority (never invents a dimension, never assumes a generic pipe bore):
//   1. the value recorded on the component itself;
//   2. the documented value on the bound Reference Library record
//      (`refId`) — this is the "documented capacity" path that was
//      previously ignored, so MWD / LWD / crossovers / tubulars with a
//      library record but a blank component ID contributed nothing;
//   3. a stabilizer's documented `throughPipeId` (its through-bore).
// Anything still unresolved is reported by name + missing field. It is
// NEVER treated as zero.
// ---------------------------------------------------------------------------
function _refRecord(c) {
  if (!c || !c.refId) return null;
  try { return findById(c.refId) || null; } catch { return null; }
}

function _posNum(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function _resolveComponentGeometry(c, ref) {
  const name = c.displayName || roleLabel(c.role) || c.role || "component";
  const out = {
    name,
    role: c.role || "",
    lengthFt: _posNum(c.lengthFt),
    pieceCount: c.pieceCount === "" || c.pieceCount == null ? null : Number(c.pieceCount),
    odIn: null, odSource: "",
    idIn: null, idSource: "",
    missing: [],
  };

  const odSelf = _posNum(c.outerDiameter);
  const odRef  = ref ? _posNum(ref.outerDiameter) : null;
  if (odSelf != null)      { out.odIn = odSelf; out.odSource = "component"; }
  else if (odRef != null)  { out.odIn = odRef;  out.odSource = "reference library"; }
  else                     { out.missing.push("outer diameter"); }

  const idSelf   = _posNum(c.innerDiameter);
  const idRef    = ref ? _posNum(ref.innerDiameter) : null;
  const idThrough = ref ? _posNum(ref.throughPipeId) : null;
  if (idSelf != null)         { out.idIn = idSelf;    out.idSource = "component"; }
  else if (idRef != null)     { out.idIn = idRef;     out.idSource = "reference library"; }
  else if (idThrough != null) { out.idIn = idThrough; out.idSource = "reference library · through-bore"; }
  else                        { out.missing.push("internal diameter / documented capacity"); }

  if (out.lengthFt == null) out.missing.push("length");
  return out;
}

// ---------------------------------------------------------------------------
// String internal volume — DrillString (DP+HWDP+XO) + BHA, in barrels.
//
// Returns { value, complete, missing[], breakdown[] }:
//   value    — the summed volume (full precision) or "" when nothing at all
//              could be computed;
//   complete — false when ANY contributing component could not be resolved,
//              so no caller can present a partial total as a complete one;
//   missing  — "<component> (<missing field>)" strings for the report/label.
// ---------------------------------------------------------------------------
function _computeStringInternalVolBbl(ds, bha) {
  let total = 0;
  let anyContribution = false;
  let complete = true;
  const missing = [];
  const breakdown = [];

  // DrillString side — use the schema's whole-record geometry pass.
  if (ds) {
    const baked = computeDrillStringGeometry(ds);
    for (const s of (baked.sections || [])) {
      breakdown.push({
        group: "drill string",
        name: s.label || s.sectionType || s.componentType || "section",
        lengthFt: s.effectiveLengthFt === "" ? null : Number(s.effectiveLengthFt),
        odIn: _posNum(s.outerDiameter),
        idIn: _posNum(s.innerDiameter),
        idSource: "drill string section",
        capacityBblPerFt: s.internalCapacityBblPerFt === "" ? null : Number(s.internalCapacityBblPerFt),
        internalVolBbl: s.internalVolumeBbl === "" ? null : Number(s.internalVolumeBbl),
        included: s.internalVolumeBbl !== "" && s.internalVolumeBbl != null,
        missing: [],
      });
    }
    const raw = baked.summary?.totalInternalVolumeBbl;
    if (raw === "" || raw == null) {
      if (Array.isArray(ds.sections) && ds.sections.length > 0) {
        // The whole-record summary blanks out when any section is missing
        // length / ID. Keep that strictness, but say so instead of
        // publishing a silent partial.
        complete = false;
        missing.push("drill string section(s) missing length or internal diameter");
      }
    } else {
      const v = Number(raw);
      if (Number.isFinite(v)) { total += v; anyContribution = true; }
    }
  }

  // BHA side — same ID²/1029.4 identity, now with documented-capacity
  // resolution and explicit reporting of what could not be resolved.
  if (bha && Array.isArray(bha.components)) {
    for (const c of bha.components) {
      const g = _resolveComponentGeometry(c, _refRecord(c));
      const canCompute = g.idIn != null && g.lengthFt != null;
      const cap = g.idIn != null ? (g.idIn * g.idIn) / BBL_PER_FT_DIVISOR : null;
      const vol = canCompute ? g.lengthFt * cap : null;
      if (canCompute) { total += vol; anyContribution = true; }
      else {
        complete = false;
        const fields = g.missing.filter((m) => m !== "outer diameter");
        missing.push(`${g.name} (${fields.join(", ") || "geometry"})`);
      }
      breakdown.push({
        group: "BHA",
        name: g.name, role: g.role,
        lengthFt: g.lengthFt, odIn: g.odIn, odSource: g.odSource,
        idIn: g.idIn, idSource: g.idSource,
        capacityBblPerFt: cap,
        internalVolBbl: vol,
        included: canCompute,
        missing: g.missing,
      });
    }
  }

  return {
    value: anyContribution ? total : "",
    complete: anyContribution ? complete : false,
    missing,
    breakdown,
  };
}

function _firstFinite(...values) {
  for (const v of values) {
    if (v === "" || v == null) continue;
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function _emptyState() {
  return {
    wellId: "",
    well: null,
    sources: { bhaId: "", drillStringId: "", rigSheetId: "" },
    snapshot: {
      currentMdFt: "", currentTvdFt: "", holeSize: "",
      casingShoeMd: "", casingShoeTvdFt: "",
      stringInternalVolBbl: "",
      bitToShoeVolBbl: "",
      shoeToSurfaceVolBbl: "",
      casingInnerDiameterIn: "",
    },
  };
}

// ---------------------------------------------------------------------------
// Annular volumes — bit → shoe (open-hole) and shoe → surface (cased).
//
// Walks the unified stack of BHA components ++ DrillString sections from the
// bit going UP to the rig floor. For each segment, splits its overlap into
// the open-hole annulus (between bitMd and shoeMd, capped by holeSize) and
// the cased annulus (between shoeMd and 0, capped by the casing ID).
//
// Pure. Returns:
//   { bitToShoeVolBbl, shoeToSurfaceVolBbl, casingId }
// Each volume cell falls back to "" if any required input is missing.
// `casingId` is exposed for the UI to surface to the user.
// ---------------------------------------------------------------------------
function _computeAnnularVolumes({ well, bha, ds, bitMd, shoeMd, holeSizeIn, holeSizeIssue }) {
  const _bit = Number(bitMd);
  const _shoe = Number(shoeMd);
  const _hole = Number(holeSizeIn);
  const blank = {
    bitToShoeVolBbl: "", shoeToSurfaceVolBbl: "", casingId: "",
    complete: false, issues: [], assemblyLengthFt: null, breakdown: [],
  };
  if (!Number.isFinite(_bit)  || _bit  <= 0)  return { ...blank, issues: ["current depth (bit MD) unknown"] };
  if (!Number.isFinite(_shoe) || _shoe <= 0)  return { ...blank, issues: ["casing shoe depth unknown"] };
  if (!Number.isFinite(_hole) || _hole <= 0)  return { ...blank, issues: [holeSizeIssue || "open-hole diameter unknown"] };
  if (_shoe > _bit) return { ...blank, issues: ["casing shoe is deeper than the bit"] };

  // Cased annulus geometry — resolved PER casing interval (manual override
  // first, linked library record second), so each depth interval uses the
  // casing string that actually covers it instead of applying the deepest
  // string's ID to the whole shoe→surface interval.
  const csg = casingIdIntervals(well);
  const casingId = _casingIdAtDepth(csg.intervals, _shoe);
  const casingBlocked = csg.invalid.length > 0;

  // Unified segment stack from bit (deepest) up to rig floor.
  const stack = [];
  const issues = [];
  const breakdown = [];
  if (bha && Array.isArray(bha.components)) {
    for (const c of bha.components) {
      const g = _resolveComponentGeometry(c, _refRecord(c));
      if (g.lengthFt == null || g.odIn == null) {
        return {
          ...blank,
          casingId: Number.isFinite(casingId) && casingId > 0 ? casingId : "",
          issues: [`${g.name}: missing ${g.lengthFt == null ? "length" : "outer diameter"} — annular volume not computed`],
        };
      }
      stack.push({ lengthFt: g.lengthFt, odIn: g.odIn, name: g.name, group: "BHA", odSource: g.odSource });
    }
  }
  if (ds && Array.isArray(ds.sections)) {
    const baked = computeDrillStringGeometry(ds);
    for (const s of baked.sections) {
      const L  = Number(s.effectiveLengthFt);
      const od = Number(s.outerDiameter);
      if (!Number.isFinite(L)  || L  <= 0 || !Number.isFinite(od) || od <= 0) {
        return {
          ...blank,
          casingId: Number.isFinite(casingId) && casingId > 0 ? casingId : "",
          issues: [`drill string section "${s.label || s.sectionType || "section"}": missing ${Number.isFinite(L) && L > 0 ? "outer diameter" : "length"} — annular volume not computed`],
        };
      }
      stack.push({ lengthFt: L, odIn: od, name: s.label || s.sectionType || "section", group: "drill string", odSource: "drill string section" });
    }
  }
  if (stack.length === 0) return { ...blank, issues: ["no drill string or BHA bound to this well"] };

  let cursor = _bit;        // running depth (decreases as we walk up)
  let bitToShoe = 0;
  let shoeToSurface = 0;
  let shoeUnresolvedCsg = false;
  let uncoveredCasedFt = 0;
  let assemblyLengthFt = 0;

  for (const seg of stack) {
    const segBottom = cursor;
    const segTop    = cursor - seg.lengthFt;
    assemblyLengthFt += seg.lengthFt;

    // Portion in open hole: between shoeMd and bitMd.
    const ohOverlap = Math.max(0, Math.min(segBottom, _bit) - Math.max(segTop, _shoe));
    let ohVol = 0;
    if (ohOverlap > 0) {
      const cap = (_hole * _hole - seg.odIn * seg.odIn) / BBL_PER_FT_DIVISOR;
      // cap === 0 is legitimate (bit fills the hole). cap < 0 is physically
      // impossible (OD > hole) and signals bad inputs.
      if (!Number.isFinite(cap) || cap < 0) {
        return {
          ...blank,
          casingId: Number.isFinite(casingId) && casingId > 0 ? casingId : "",
          issues: [`${seg.name}: OD ${seg.odIn} in exceeds the open-hole size ${_hole} in`],
        };
      }
      ohVol = ohOverlap * cap;
      bitToShoe += ohVol;
    }

    // Portion in casing: between 0 and shoeMd — split across the casing
    // intervals that actually cover it.
    const csTop    = Math.max(segTop, 0);
    const csBottom = Math.min(segBottom, _shoe);
    const csOverlap = Math.max(0, csBottom - csTop);
    let csVol = 0;
    let csCovered = 0;
    if (csOverlap > 0) {
      if (casingBlocked) {
        shoeUnresolvedCsg = true;
      } else {
        for (const iv of csg.intervals) {
          const L = Math.max(0, Math.min(csBottom, iv.bottomFt) - Math.max(csTop, iv.topFt));
          if (L <= 0) continue;
          const cap = (iv.idIn * iv.idIn - seg.odIn * seg.odIn) / BBL_PER_FT_DIVISOR;
          if (!Number.isFinite(cap) || cap < 0) {
            return {
              ...blank,
              casingId: Number.isFinite(casingId) && casingId > 0 ? casingId : "",
              issues: [`${seg.name}: OD ${seg.odIn} in exceeds the casing ID ${iv.idIn} in (${iv.label})`],
            };
          }
          csVol += L * cap;
          csCovered += L;
        }
        if (csCovered + 1e-9 < csOverlap) {
          shoeUnresolvedCsg = true;
          uncoveredCasedFt += csOverlap - csCovered;
        }
        shoeToSurface += csVol;
      }
    }
    breakdown.push({
      group: seg.group, name: seg.name, lengthFt: seg.lengthFt,
      odIn: seg.odIn, odSource: seg.odSource,
      topMdFt: segTop, bottomMdFt: segBottom,
      openHoleLengthFt: ohOverlap, openHoleVolBbl: ohVol,
      casedLengthFt: csOverlap, casedVolBbl: csVol,
      crossesShoe: ohOverlap > 0 && csOverlap > 0,
    });
    cursor = segTop;
  }

  // Length reconciliation — the walk above only covers the depth the
  // assembly actually occupies. A string shorter than the hole leaves an
  // uncovered interval whose contents are unknown; a string longer than the
  // hole means a geometry error. Either way it is reported, never filled in
  // with an assumed component.
  const gapFt = cursor;                     // > 0: uncovered interval above the string
  if (gapFt > 1) {
    issues.push(`assembly length ${assemblyLengthFt.toFixed(1)} ft is ${gapFt.toFixed(1)} ft short of the bit depth ${_bit.toFixed(1)} ft — the annulus over that interval is NOT included`);
  } else if (gapFt < -1) {
    issues.push(`assembly length ${assemblyLengthFt.toFixed(1)} ft exceeds the bit depth ${_bit.toFixed(1)} ft by ${Math.abs(gapFt).toFixed(1)} ft — check section lengths`);
  }
  if (csg.invalid.length > 0) {
    issues.push(...csg.invalid.map((m) => `${m} — shoe→surface annulus not computed`));
  } else if (shoeUnresolvedCsg) {
    const where = uncoveredCasedFt > 0 ? ` (${uncoveredCasedFt.toFixed(1)} ft of the cased interval uncovered)` : "";
    issues.push(
      `casing internal diameter unresolved${where} — enter a manual casing ID on the Casing Program row or link a Reference Library record; shoe→surface annulus not computed`
      + (csg.missing.length ? ` · missing: ${csg.missing.join("; ")}` : "")
    );
  }

  return {
    bitToShoeVolBbl:     bitToShoe > 0 ? bitToShoe : "",
    shoeToSurfaceVolBbl: shoeUnresolvedCsg ? "" : (shoeToSurface > 0 ? shoeToSurface : ""),
    casingId: Number.isFinite(casingId) && casingId > 0 ? casingId : "",
    complete: issues.length === 0,
    issues,
    assemblyLengthFt,
    breakdown,
  };
}

// Governing casing ID at a given depth — the innermost string covering it.
// Returns NaN when no casing interval covers that depth.
function _casingIdAtDepth(intervals, depthFt) {
  let best = NaN;
  for (const iv of intervals || []) {
    if (depthFt >= iv.topFt && depthFt <= iv.bottomFt) {
      if (!Number.isFinite(best) || iv.idIn < best) best = iv.idIn;
    }
  }
  return best;
}
